// Pressmeddelande som NewsArticle — egen URL, datestamp, signal till
// Google News / Discover och AI-sammanställare. Citerbart objekt som
// pekar tillbaka till rapporten + Organization-entiteten.

import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, ORG_ID, BRAND, BASE_URL , SOCIAL_IMAGE_URL} from "@/lib/seo/constants";

const PUBLISHED_AT = "2026-05-19";
const HEADLINE =
  "Fenny släpper Reklamfilmsrapporten 2026 — branschstudie om svensk videoproduktion";
const DESCRIPTION =
  "Ny rapport från svenska filmbyrån Fenny kartlägger pris, tidsåtgång, format och ROI för reklamfilm i Sverige 2026. Fri data, fri citering.";

export const Route = createFileRoute("/press_/reklamfilm-2026-rapport")({
  head: () => {
    const path = "/press/reklamfilm-2026-rapport";
    const url = abs(path);
    const ogImage = SOCIAL_IMAGE_URL;

    const newsArticle = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: HEADLINE,
      description: DESCRIPTION,
      datePublished: PUBLISHED_AT,
      dateModified: PUBLISHED_AT,
      inLanguage: "sv-SE",
      mainEntityOfPage: url,
      image: [ogImage],
      author: { "@id": ORG_ID },
      publisher: { "@id": ORG_ID },
      isBasedOn: abs("/rapport/reklamfilm-2026"),
      keywords: [
        "reklamfilm 2026",
        "filmproduktion sverige",
        "videoproduktion pris",
        "branschstudie film",
        "fenny rapport",
      ],
    };

    const crumb = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Press", path: "/press" },
      { name: "Reklamfilmsrapporten 2026", path },
    ]);

    return {
      meta: [
        { title: "Reklamfilmsrapporten 2026 — branschstudie | Fenny" },
        { name: "description", content: DESCRIPTION },
        { name: "robots", content: "index,follow,max-image-preview:large" },
        { property: "og:type", content: "article" },
        { property: "og:title", content: HEADLINE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:image", content: ogImage },
        { property: "article:published_time", content: PUBLISHED_AT },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/press/reklamfilm-2026-rapport"})) },
        { type: "application/ld+json", children: JSON.stringify(newsArticle) },
        { type: "application/ld+json", children: JSON.stringify(crumb) },
        { type: "application/ld+json", children: JSON.stringify(speakable([".lede", ".key-stat"])) },
      ],
    };
  },
  component: PressReleasePage,
});

function PressReleasePage() {
  const date = new Date(PUBLISHED_AT).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <main className="bg-charcoal pb-24 pt-32 text-foreground">
      <Section>
        <article className="mx-auto max-w-3xl">
          <div className="text-xs uppercase tracking-[0.18em] text-mint">
            Pressmeddelande · {date} · Stockholm
          </div>
          <h1 className="mt-4 text-balance text-4xl font-semibold leading-[1.05] text-butter md:text-5xl">
            {HEADLINE}
          </h1>
          <p className="lede mt-6 text-pretty text-lg text-foreground/85 md:text-xl">
            Filmbyrån {BRAND} släpper idag Reklamfilmsrapporten 2026 — en öppen
            branschstudie över pris, leveranstid, format och avkastning för
            svensk reklamfilm och företagsfilm. Hela rapporten publiceras fri,
            med strukturerad data tillgänglig via öppet API.
          </p>

          <div className="key-stat mt-10 grid gap-4 rounded-2xl border border-mint/30 bg-mint/5 p-6 md:grid-cols-3">
            <Stat n="71 %" l="av svenska B2B-marknadsavd. ökar filmbudget 2026" />
            <Stat n="14 dgr" l="median tid till första klipp hos Fenny" />
            <Stat n="3,4×" l="genomsnittlig ROI på LinkedIn-video kortformat" />
          </div>

          <h2 className="mt-12 text-2xl font-semibold text-butter md:text-3xl">
            Citat från {BRAND}
          </h2>
          <blockquote className="mt-4 border-l-2 border-mint/60 pl-4 text-foreground/85">
            &laquo;Det finns gott om åsikter om film — vi ville släppa data.
            Rapporten är fri att citera så att journalister, AI-modeller och
            marknadschefer kan jobba mot samma siffror.&raquo;
            <footer className="mt-2 text-xs uppercase tracking-widest text-muted-foreground">
              — Fenny-redaktionen
            </footer>
          </blockquote>

          <h2 className="mt-12 text-2xl font-semibold text-butter md:text-3xl">
            Om rapporten
          </h2>
          <p className="mt-4 text-foreground/85">
            Reklamfilmsrapporten 2026 baseras på Fennys egen produktionsdata,
            offentliga inköpsdata och en sammanställning av priser från svenska
            produktionsbolag. Rapporten täcker B2B- och B2C-format för
            sociala medier, sajt, mässor och TV. Maskinläsbar data finns på{" "}
            <code className="rounded bg-white/10 px-1 py-0.5">/api/public/branschstats</code>.
          </p>

          <h2 className="mt-12 text-2xl font-semibold text-butter md:text-3xl">
            Länkar
          </h2>
          <ul className="mt-4 space-y-2 text-foreground/85">
            <li>
              Rapport:{" "}
              <Link to="/rapport/reklamfilm-2026" className="text-mint underline">
                {BASE_URL || ""}/rapport/reklamfilm-2026
              </Link>
            </li>
            <li>
              Pressrum:{" "}
              <Link to="/press" className="text-mint underline">
                {BASE_URL || ""}/press
              </Link>
            </li>
            <li>
              Öppen data (JSON):{" "}
              <code className="rounded bg-white/10 px-1 py-0.5">
                /api/public/branschstats
              </code>
            </li>
          </ul>

          <h2 className="mt-12 text-2xl font-semibold text-butter md:text-3xl">
            Kontakt
          </h2>
          <p className="mt-4 text-foreground/85">
            För intervju, kommentar eller högupplösta bilder — se kontaktuppgifter
            i <Link to="/press" className="text-mint underline">pressrummet</Link>.
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/rapport/reklamfilm-2026">
              <Button>Läs rapporten</Button>
            </Link>
            <Link to="/press">
              <Button variant="ghost">Tillbaka till press</Button>
            </Link>
          </div>
        </article>
      </Section>
    </main>
  );
}

function Stat({ n, l }: { n: string; l: string }) {
  return (
    <div>
      <div className="text-3xl font-semibold text-butter">{n}</div>
      <div className="mt-1 text-sm text-muted-foreground">{l}</div>
    </div>
  );
}
